import React, {useState, useEffect, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {isEmpty as _isEmpty} from 'lodash';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Header, Segment} from 'semantic-ui-react';
import {useMutation} from '@apollo/client';

import Authorization from '@appComponents/Authorization';
import HeaderRenderer from '@appComponents/HeaderRenderer';
import {getLink} from '@appComponents/Link';
import {ARGOS_JOB} from '@constants/resources';
import {routes} from '@constants/routes';
import {EVENT_SPORTRADAR_TYPE, SECURITY_PRIVILEGES_READ} from '@constants/variables';
import {GetEventArgosJobs} from '@graphql/argosJob/query';
import {ArgosButtons} from '@modules/events/components/ArgosButtons';
import {ArgosTitle} from '@modules/events/components/ArgosTitle';
import {ArgosSubpartsContent} from '@modules/events/components/ArgosSubpartsContent';
import {ArgosJobEditFormWrapper} from '@modules/events/components/ArgosJobEditFormWrapper';
import {ArgosJobRouteGuard} from '@modules/events/components/ArgosJobRouteGuard';
import {ArgosJobHistoryModal} from '@modules/events/components/ArgosSubpartsContent/ArgosJobHistoryModal';
import {ArgosStreamHistoryModal} from '@modules/events/components/ArgosSubpartsContent/ArgosStreamHistoryModal';
import ArgosJobForm from '@modules/events/forms/ArgosJob/ArgosJobForm';
import ArgosJobModel from '@modules/events/forms/ArgosJob/ArgosJobModel';
import {createForm} from '@utils/forms';
import useQueryCustom from '@utils/hooks/useQueryCustom';
import {useModalRouting} from '@utils/hooks/useModalRouting';
import {convertToInt} from '@utils/helpers';
import mapModulesToProps from '@utils/mapModulesToProps';
import * as MESSAGES from '@constants/messages';
import {DeleteArgosJob} from '@graphql/argosJob/mutation';
import {showMessageBox} from '@utils/messageBox';
import {hideModal} from '@utils/modal';

import EventsContentPanel from '../components/EventsContentPanel';

const Argos = ({Modal}) => {
    const {id: eventId, argosJobId} = useParams();
    const argosJobsIndexUrl = getLink('events.argosJobs.index', {id: eventId});

    const {data, loading, refetch} = useQueryCustom({
        query: GetEventArgosJobs,
        options: {variables: {id: eventId}},
    });

    const argosJobs = data?.argosJobs || [];

    const [expandedPanels, setExpandedPanels] = useState({});
    const [expandTimestamp, setExpandTimestamp] = useState(Date.now());

    useEffect(() => {
        if (!_isEmpty(argosJobs)) {
            const initial = {};
            argosJobs.forEach(job => {
                initial[job.id] = true;
            });
            setExpandedPanels(initial);
        }
    }, [argosJobs]);

    const expandedAll = !_isEmpty(expandedPanels) && Object.values(expandedPanels).every(Boolean);

    const handleExpandCollapse = () => {
        const next = {};
        const target = !expandedAll;
        argosJobs.forEach(job => {
            next[job.id] = target;
        });
        setExpandedPanels(next);
        setExpandTimestamp(Date.now());
    };

    const handlePanelExpand = (jobId, expanded) => {
        setExpandedPanels(prev => ({...prev, [jobId]: expanded}));
    };

    const routesMap = useMemo(() => [
        {
            path: routes.events.argosJobs.add.path,
            render: () => {
                const Form = createForm(
                    ArgosJobModel,
                    ArgosJobForm,
                    {
                        optionsVariables: {eventId: convertToInt(eventId)},
                        eventStartDatetime: data?.event?.start_datetime,
                        eventEndDatetime: data?.event?.end_datetime,
                        isSportradarType: (convertToInt(data?.event?.event_type?.id) === EVENT_SPORTRADAR_TYPE),
                    }
                );

                return <Form/>;
            },
            size: 'large',
        },
        {
            path: routes.events.argosJobs.edit.path,
            render: () => (
                <ArgosJobEditFormWrapper
                    eventId={eventId}
                    argosJobId={argosJobId}
                    eventData={data?.event}
                    jobs={argosJobs}
                />
            ),
            size: 'large',
        },
        {
            path: routes.events.argosJobs.jobHistory.path,
            render: () => {
                const job = argosJobs.find(j => String(j.id) === String(argosJobId));

                return (
                    <ArgosJobRouteGuard eventId={eventId} argosJobId={argosJobId} jobs={argosJobs}>
                        <ArgosJobHistoryModal jobId={argosJobId} job={job} />
                    </ArgosJobRouteGuard>
                );
            },
            size: 'large',
        },
        {
            path: routes.events.argosJobs.streamHistory.path,
            render: () => {
                const job = argosJobs.find(j => String(j.id) === String(argosJobId));

                return (
                    <ArgosJobRouteGuard eventId={eventId} argosJobId={argosJobId} jobs={argosJobs}>
                        <ArgosStreamHistoryModal jobId={argosJobId} job={job} />
                    </ArgosJobRouteGuard>
                );
            },
            size: 'large',
        },
    ], [argosJobId, argosJobs, data?.event, eventId]);

    useModalRouting({
        shouldHandleModal: !loading && !!data,
        routesMap,
        indexUrl: argosJobsIndexUrl,
        queryToRefetch: ['GetEventArgosJobs'],
    });

    const [deleteArgosJobMutation] = useMutation(DeleteArgosJob, {
        refetchQueries: ['GetEventArgosJobs'],
    });

    const handleDeleteArgosJob = (job) => {
        Modal.setModalConfirmation({
            header: <Header icon='trash' content={'Delete Argos job'}/>,
            text: `Are you sure you want to delete Argos job ${job?.id}?`,
            onYes: () => deleteArgosJob(job),
        });
    };

    const deleteArgosJob = (job) => {
        deleteArgosJobMutation({variables: {id: job.id}})
            .then(() => {
                showMessageBox(
                    'argosJobMessage',
                    MESSAGES.DELETED('argos job'),
                    null,
                    'success'
                );
                hideModal();
            })
            .catch((error) => {
                showMessageBox(
                    'argosJobMessage',
                    'There was an error while deleting the argos job.',
                    `${error}`,
                    'error'
                );
                hideModal();
            });
    };

    return (
        <div>
            <HeaderRenderer
                messagesBoxNames='argosJobMessage'
                buttons={ArgosButtons}
                buttonsProps={{
                    shouldExpand: !expandedAll,
                    onClick: handleExpandCollapse,
                    showExpandCollapse: !_isEmpty(argosJobs),
                    id: eventId,
                }}
            />
            <Segment basic loading={loading}>
                <Authorization authorization={{[ARGOS_JOB]: SECURITY_PRIVILEGES_READ}}>
                    {argosJobs.map(job => (
                        <EventsContentPanel
                            key={job.id}
                            id={job.id}
                            setContentExpandedState={() => {}}
                            shouldExpand={expandedPanels[job.id]}
                            shouldExpandTimestamp={expandTimestamp}
                            title={
                                <ArgosTitle
                                    eventId={eventId}
                                    job={job}
                                    onDelete={() => handleDeleteArgosJob(job)}
                                    onStartStopSuccess={() => refetch()}
                                />
                            }
                            subparts={[
                                <ArgosSubpartsContent
                                    key={job.id}
                                    data={job}
                                    eventStartDatetime={data?.event?.start_datetime}
                                    eventEndDatetime={data?.event?.end_datetime}
                                    eventId={eventId}
                                    jobId={job?.id}
                                />,
                            ]}
                            onExpand={handlePanelExpand}
                        />
                    ))}
                </Authorization>
            </Segment>
        </div>
    );
};

Argos.propTypes = {
    Modal: PropTypes.shape({
        setModalConfirmation: PropTypes.func.isRequired,
    }),
};

export default connect(null, mapModulesToProps(['Modal']))(Argos);
